/**
 * Service worker for offline support and asset caching
 */

export interface ServiceWorkerConfig {
  cacheName: string;
  version: string;
  assets: string[];
  apiEndpoints: string[];
  offlinePage?: string;
}

export class ServiceWorkerManager {
  private config: ServiceWorkerConfig;

  constructor(config: ServiceWorkerConfig) {
    this.config = config;
  }

  /**
   * Generate service worker script
   */
  generateServiceWorkerScript(): string {
    return `
// Service Worker for Fanbasis Checkout SDK
// Version: ${this.config.version}

const CACHE_NAME = '${this.config.cacheName}-${this.config.version}';
const ASSETS = ${JSON.stringify(this.config.assets)};
const API_ENDPOINTS = ${JSON.stringify(this.config.apiEndpoints)};
const OFFLINE_PAGE = ${JSON.stringify(this.config.offlinePage || '/offline.html')};

// Install event - cache assets
self.addEventListener('install', (event) => {
  event.waitUntil(
    caches.open(CACHE_NAME)
      .then((cache) => {
        console.log('Caching assets:', ASSETS);
        return cache.addAll(ASSETS);
      })
      .then(() => {
        console.log('Service worker installed');
        return self.skipWaiting();
      })
      .catch((error) => {
        console.error('Service worker install failed:', error);
      })
  );
});

// Activate event - clean up old caches
self.addEventListener('activate', (event) => {
  event.waitUntil(
    caches.keys()
      .then((cacheNames) => {
        return Promise.all(
          cacheNames.map((cacheName) => {
            if (cacheName !== CACHE_NAME) {
              console.log('Deleting old cache:', cacheName);
              return caches.delete(cacheName);
            }
          })
        );
      })
      .then(() => {
        console.log('Service worker activated');
        return self.clients.claim();
      })
  );
});

// Fetch event - handle requests
self.addEventListener('fetch', (event) => {
  const { request } = event;
  const url = new URL(request.url);

  // Handle API requests with network-first strategy
  if (API_ENDPOINTS.some(endpoint => url.pathname.includes(endpoint))) {
    event.respondWith(handleApiRequest(request));
    return;
  }

  // Handle asset requests with cache-first strategy
  if (ASSETS.some(asset => url.pathname.includes(asset))) {
    event.respondWith(handleAssetRequest(request));
    return;
  }

  // Handle other requests with network-first strategy
  event.respondWith(handleOtherRequest(request));
});

// API request handler - network first, cache fallback
async function handleApiRequest(request) {
  try {
    const response = await fetch(request);
    
    // Cache successful API responses
    if (response.ok) {
      const cache = await caches.open(CACHE_NAME);
      cache.put(request, response.clone());
    }
    
    return response;
  } catch (error) {
    console.log('API request failed, trying cache:', request.url);
    
    // Try to get from cache
    const cachedResponse = await caches.match(request);
    if (cachedResponse) {
      return cachedResponse;
    }
    
    // Return offline page if available
    const offlineResponse = await caches.match(OFFLINE_PAGE);
    if (offlineResponse) {
      return offlineResponse;
    }
    
    throw error;
  }
}

// Asset request handler - cache first, network fallback
async function handleAssetRequest(request) {
  const cachedResponse = await caches.match(request);
  if (cachedResponse) {
    return cachedResponse;
  }

  try {
    const response = await fetch(request);
    if (response.ok) {
      const cache = await caches.open(CACHE_NAME);
      cache.put(request, response.clone());
    }
    return response;
  } catch (error) {
    console.error('Asset request failed:', request.url, error);
    throw error;
  }
}

// Other request handler - network first, cache fallback
async function handleOtherRequest(request) {
  try {
    const response = await fetch(request);
    return response;
  } catch (error) {
    console.log('Request failed, trying cache:', request.url);
    
    const cachedResponse = await caches.match(request);
    if (cachedResponse) {
      return cachedResponse;
    }
    
    throw error;
  }
}

// Background sync for offline actions
self.addEventListener('sync', (event) => {
  if (event.tag === 'background-sync') {
    event.waitUntil(handleBackgroundSync());
  }
});

async function handleBackgroundSync() {
  try {
    // Process any pending offline actions
    console.log('Processing background sync');
    
    // Get pending actions from IndexedDB or other storage
    // Process them and sync with server
    
  } catch (error) {
    console.error('Background sync failed:', error);
  }
}

// Push notification handler
self.addEventListener('push', (event) => {
  if (event.data) {
    const data = event.data.json();
    
    event.waitUntil(
      self.registration.showNotification(data.title, {
        body: data.body,
        icon: data.icon,
        badge: data.badge,
        data: data.data,
      })
    );
  }
});

// Notification click handler
self.addEventListener('notificationclick', (event) => {
  event.notification.close();
  
  event.waitUntil(
    clients.openWindow(event.notification.data?.url || '/')
  );
});
`;
  }

  /**
   * Register service worker
   */
  async register(swPath: string): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers not supported');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register(swPath);
      console.log('Service worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service worker registration failed:', error);
      return null;
    }
  }

  /**
   * Unregister service worker
   */
  async unregister(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.unregister();
        console.log('Service worker unregistered');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Service worker unregistration failed:', error);
      return false;
    }
  }

  /**
   * Check if service worker is active
   */
  async isActive(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      return registration?.active !== undefined;
    } catch (error) {
      console.error('Service worker status check failed:', error);
      return false;
    }
  }
}

/**
 * Default service worker configuration
 */
export const DEFAULT_SW_CONFIG: ServiceWorkerConfig = {
  cacheName: 'fanbasis-checkout',
  version: '1.0.0',
  assets: [
    '/checkout.js',
    '/checkout.css',
    '/assets/',
  ],
  apiEndpoints: [
    '/api/checkout',
    '/api/payments',
  ],
  offlinePage: '/offline.html',
};

// Global service worker manager instance
export const swManager = new ServiceWorkerManager(DEFAULT_SW_CONFIG); 